import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './Home.css';

const INDEX_ORDER = ['tsx', 'sp500', 'nasdaq', 'dow', 'cadusd', 'eurcad', 'cadjpy'];

function formatIndexPrice(value, digits = 2) {
    if (value == null || Number.isNaN(value)) return '—';
    return new Intl.NumberFormat(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    }).format(value);
}

function formatChange(value, digits = 2) {
    if (value == null || Number.isNaN(value)) return null;
    const sign = value > 0 ? '+' : value < 0 ? '−' : '';
    const abs = Math.abs(value);
    return `${sign}${new Intl.NumberFormat(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    }).format(abs)}`;
}

function formatChangePercent(value) {
    if (value == null || Number.isNaN(value)) return null;
    const sign = value > 0 ? '+' : value < 0 ? '−' : '';
    return `${sign}${Math.abs(value).toFixed(2)}%`;
}

function formatQuoteTime(iso) {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
    });
}

function formatHighDate(dateStr) {
    if (!dateStr) return null;
    const d = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, { dateStyle: 'medium' });
}

// How far a reference value (all-time high / YTD low) sits above or below the current price, as a percentage.
function formatPercentOffCurrent(referenceValue, currentValue) {
    if (referenceValue == null || currentValue == null || Number.isNaN(referenceValue) || Number.isNaN(currentValue) || currentValue === 0) {
        return null;
    }
    const pct = ((referenceValue - currentValue) / currentValue) * 100;
    return formatChangePercent(pct);
}

function IndexCard({ row }) {
    if (row.error) {
        return (
            <article className="home-index-card home-index-card--error">
                <h2 className="home-index-name">{row.name}</h2>
                <p className="home-index-error">Quote unavailable</p>
            </article>
        );
    }

    const up = row.change != null && row.change > 0;
    const down = row.change != null && row.change < 0;
    const changeClass = up
        ? 'home-index-change home-index-change--up'
        : down
            ? 'home-index-change home-index-change--down'
            : 'home-index-change';

    // FX rates need more precision than index levels (e.g. 0.7312 vs 25,000.12).
    const digits = row.kind === 'currency' ? 4 : 2;
    const changeStr = formatChange(row.change, digits);
    const pctStr = formatChangePercent(row.change_percent);
    const quoteTimeStr = formatQuoteTime(row.quote_time);
    const highDateStr = formatHighDate(row.high_date);
    const ytdLowDateStr = formatHighDate(row.ytd_low_date);
    const highPctOffCurrent = formatPercentOffCurrent(row.high, row.price);
    const ytdLowPctOffCurrent = formatPercentOffCurrent(row.ytd_low, row.price);

    return (
        <article className="home-index-card">
            <h2 className="home-index-name">{row.name}</h2>
            <p className="home-index-price">{formatIndexPrice(row.price, digits)}</p>
            {row.market_open && changeStr != null && (
                <p className={changeClass}>
                    {changeStr}
                    {pctStr != null && <span className="home-index-change-pct"> ({pctStr})</span>}
                </p>
            )}
            {row.market_open && (
                <p className="home-index-status home-index-status--open">Market open</p>
            )}
            {!row.market_open && (
                <p className="home-index-status">Market closed</p>
            )}
            {quoteTimeStr && (
                <p className="home-index-time">As of {quoteTimeStr}</p>
            )}
            {row.high != null && (
                <p className="home-index-high">
                    All-time high: {formatIndexPrice(row.high, digits)}
                    {highDateStr && <span className="home-index-high-date"> ({highDateStr})</span>}
                    {highPctOffCurrent != null && (
                        <span className="home-index-high-pct"> · {highPctOffCurrent} off current</span>
                    )}
                </p>
            )}
            {row.ytd_low != null && (
                <p className="home-index-high">
                    YTD low: {formatIndexPrice(row.ytd_low, digits)}
                    {ytdLowDateStr && <span className="home-index-high-date"> ({ytdLowDateStr})</span>}
                    {ytdLowPctOffCurrent != null && (
                        <span className="home-index-high-pct"> · {ytdLowPctOffCurrent} off current</span>
                    )}
                </p>
            )}
        </article>
    );
}

const Home = () => {
    const { user, loading } = useAuth();
    const [indices, setIndices] = useState([]);
    const [indicesLoading, setIndicesLoading] = useState(true);
    const [indicesError, setIndicesError] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setIndicesLoading(true);
            setIndicesError('');
            try {
                const [snapshotRes, highsRes] = await Promise.all([
                    fetch('/api/markets/indices/snapshot'),
                    fetch('/api/markets/indices/highs'),
                ]);
                const data = await snapshotRes.json();
                if (!snapshotRes.ok) throw new Error(data.error || 'Failed to load market quotes');
                const list = Array.isArray(data.indices) ? data.indices : [];
                const byKey = Object.fromEntries(list.map(row => [row.key, row]));

                if (highsRes.ok) {
                    const highsData = await highsRes.json();
                    const highsList = Array.isArray(highsData.indices) ? highsData.indices : [];
                    for (const h of highsList) {
                        if (byKey[h.key]) {
                            byKey[h.key] = {
                                ...byKey[h.key],
                                high: h.high,
                                high_date: h.date,
                                ytd_low: h.ytd_low,
                                ytd_low_date: h.ytd_low_date,
                            };
                        }
                    }
                }

                const ordered = INDEX_ORDER.map(k => byKey[k]).filter(Boolean);
                if (!cancelled) setIndices(ordered);
            } catch (err) {
                if (!cancelled) setIndicesError(err.message);
            } finally {
                if (!cancelled) setIndicesLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    let status = null;
    if (!loading) {
        status = user
            ? `You are logged in as ${user.username}.`
            : 'You are not logged in.';
    }

    return (
        <div className="home">
            <h1>Welcome to DonAllison.ca</h1>
            {status && <p className="home-status">{status}</p>}

            <section className="home-indices" aria-labelledby="home-indices-heading">
                <h2 id="home-indices-heading" className="home-indices-heading">
                    Major indices
                </h2>
                {indicesLoading && (
                    <p className="home-indices-muted">Loading market quotes…</p>
                )}
                {indicesError && (
                    <p className="alert-error" role="alert">{indicesError}</p>
                )}
                {!indicesLoading && !indicesError && indices.length > 0 && (
                    <div className="home-indices-grid">
                        {indices.map(row => (
                            <IndexCard key={row.key} row={row} />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};

export default Home;
